import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CheckCircle2, ClipboardList, Download, Eye, FileX2, Loader2, RefreshCw, Send, X } from "lucide-react";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import Pagination from "@/components/ui/Pagination";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

type AssessmentStatus =
  | "submitted"
  | "report_generating"
  | "report_ready"
  | "report_sent"
  | "applied";

type ProbabilityBand = "reach" | "competitive" | "safe" | "guaranteed";

interface AssessmentStudent {
  id: string;
  name: string | null;
  phoneNumber: string;
}

interface AssessmentListRow {
  id: string;
  studentId: string;
  catPercentile: string | number;
  passId: string;
  status: AssessmentStatus;
  createdAt: string;
  student: AssessmentStudent | null;
}

interface AssessmentCollegeResult {
  id: string;
  collegeId: string;
  band: ProbabilityBand;
  score: string | number;
  narrative: string | null;
  feeWaiverPercent: string | number | null;
  feeSavingsInr: string | number | null;
  college: { id: string; name: string } | null;
}

interface AssessmentDetail extends AssessmentListRow {
  academicPercentage: string | number | null;
  budgetMax: string | number | null;
  profileScore: string | number | null;
  totalFeeSavingsInr: string | number | null;
  overallSummary: string | null;
  actionPlan: string[] | null;
  preferredCollegeIds: string[];
  results: AssessmentCollegeResult[];
}

const STATUS_META: Record<AssessmentStatus, { label: string; className: string }> = {
  submitted: { label: "Submitted", className: "bg-gray-100 text-gray-700" },
  report_generating: { label: "Generating", className: "bg-blue-100 text-blue-700" },
  report_ready: { label: "Report Ready", className: "bg-green-100 text-green-700" },
  report_sent: { label: "Report Sent", className: "bg-purple-100 text-purple-700" },
  applied: { label: "Applied", className: "bg-emerald-100 text-emerald-700" },
};

const BAND_META: Record<ProbabilityBand, { label: string; className: string }> = {
  reach: { label: "Reach", className: "bg-red-100 text-red-700" },
  competitive: { label: "Competitive", className: "bg-amber-100 text-amber-700" },
  safe: { label: "Safe", className: "bg-blue-100 text-blue-700" },
  guaranteed: { label: "Guaranteed", className: "bg-green-100 text-green-700" },
};

const STATUS_ORDER: AssessmentStatus[] = [
  "submitted",
  "report_generating",
  "report_ready",
  "report_sent",
  "applied",
];

const PAGE_SIZE = 20;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toString() : "—";
};

const MbaReadiness: React.FC = () => {
  const [assessments, setAssessments] = useState<AssessmentListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | AssessmentStatus>("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AssessmentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sendFeedback, setSendFeedback] = useState<{ id: string; ok: boolean; message: string } | null>(null);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(PAGE_SIZE),
      };
      if (statusFilter !== "all") params.status = statusFilter;

      const data = await api.get<{
        items: AssessmentListRow[];
        total: number;
        totalPages: number;
      }>("/mba-readiness/admin/assessments", params);
      setAssessments(data?.items ?? []);
      setTotalPages(data?.totalPages ?? 1);
      setTotal(data?.total ?? 0);
    } catch {
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    void fetchAssessments();
  }, [fetchAssessments]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    void (async () => {
      try {
        const data = await api.get<AssessmentDetail>(
          `/mba-readiness/admin/assessments/${selectedId}`,
        );
        setDetail(data ?? null);
      } catch {
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [selectedId]);

  const handleSendReport = useCallback(
    async (id: string) => {
      setSendingId(id);
      setSendFeedback(null);
      try {
        const updated = await api.post<AssessmentListRow>(
          `/mba-readiness/admin/assessments/${id}/send-report`,
        );
        setSendFeedback({ id, ok: true, message: "Sent via WhatsApp" });
        // Reflect the new status inline without a full refetch.
        setAssessments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status: updated?.status ?? "report_sent" } : a)),
        );
        if (selectedId === id) {
          setDetail((prev) => (prev ? { ...prev, status: updated?.status ?? "report_sent" } : prev));
        }
      } catch (err) {
        setSendFeedback({
          id,
          ok: false,
          message: err instanceof Error ? err.message : "Failed to send",
        });
      } finally {
        setSendingId(null);
      }
    },
    [selectedId],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1">
            <ClipboardList className="h-6 w-6 shrink-0 text-primary" />
            MBA Readiness Assessments
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Submitted assessments and their MBA readiness reports. Reports send
            automatically on submission — use Send/Resend here to retry a
            failed delivery or re-send on request.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as "all" | AssessmentStatus);
              setPage(1);
            }}
            className="min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All statuses</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_META[s].label}
              </option>
            ))}
          </select>
          <button
            onClick={() => void fetchAssessments()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="flex items-center justify-center p-2.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={cn("h-5 w-5", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : assessments.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <FileX2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No assessments found</p>
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <ul className="divide-y divide-gray-200 md:hidden">
              {assessments.map((a) => (
                <li
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  className="p-4 space-y-2 active:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {a.student?.name ?? "Unknown"}
                      </p>
                      {a.student?.phoneNumber && (
                        <p className="text-xs text-gray-500 truncate">
                          {a.student.phoneNumber}
                        </p>
                      )}
                    </div>
                    <span
                      className={cn(
                        "inline-flex shrink-0 px-2 py-1 rounded-full text-xs font-medium",
                        STATUS_META[a.status].className,
                      )}
                    >
                      {STATUS_META[a.status].label}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span>CAT Percentile: {formatNumber(a.catPercentile)}</span>
                    <span>{formatDate(a.createdAt)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                    <SendReportButton
                      assessmentId={a.id}
                      status={a.status}
                      sending={sendingId === a.id}
                      feedback={sendFeedback}
                      onSend={handleSendReport}
                      size="xs"
                    />
                    <ViewReportButton assessmentId={a.id} size="xs" />
                    <DownloadReportButton assessmentId={a.id} size="xs" />
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop / tablet: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CAT Percentile
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assessments.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {a.student?.name ?? "Unknown"}
                        </div>
                        {a.student?.phoneNumber && (
                          <div className="text-xs text-gray-500">
                            {a.student.phoneNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatNumber(a.catPercentile)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                            STATUS_META[a.status].className,
                          )}
                        >
                          {STATUS_META[a.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(a.createdAt)}
                      </td>
                      <td
                        className="px-4 py-4 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-wrap gap-2">
                          <SendReportButton
                            assessmentId={a.id}
                            status={a.status}
                            sending={sendingId === a.id}
                            feedback={sendFeedback}
                            onSend={handleSendReport}
                          />
                          <ViewReportButton assessmentId={a.id} />
                          <DownloadReportButton assessmentId={a.id} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && (
          <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              total={total}
              pageSize={PAGE_SIZE}
              itemLabel="assessments"
            />
          </div>
        )}
      </div>

      {selectedId && (
        <AssessmentDetailModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setSelectedId(null)}
          sending={sendingId === selectedId}
          feedback={sendFeedback}
          onSend={handleSendReport}
        />
      )}
    </div>
  );
};

interface DownloadReportButtonProps {
  assessmentId: string;
  size?: "sm" | "xs";
}

/**
 * Fetches a fresh signed URL on each click rather than reusing
 * `detail.reportUrl` — that one is presigned once at generation time and is
 * long expired by the time an admin opens this later, so it's not exposed
 * to this component at all.
 */
const DownloadReportButton: React.FC<DownloadReportButtonProps> = ({ assessmentId, size = "sm" }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { url } = await api.get<{ url: string }>(
        `/mba-readiness/admin/assessments/${assessmentId}/report-url`,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      setError(err?.message ?? "Could not get the report link");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        disabled={loading}
        className={cn(
          "inline-flex items-center gap-1.5 font-medium rounded-md border px-2.5 py-1.5 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed",
          size === "sm" ? "text-sm" : "text-xs",
        )}
      >
        {loading ? (
          <Loader2 className={cn("animate-spin", size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3")} />
        ) : (
          <Download className={size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3"} />
        )}
        Download PDF
      </button>
      {error && (
        <span className="inline-flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {error}
        </span>
      )}
    </div>
  );
};

/**
 * Same fresh-signed-URL approach as DownloadReportButton, but renders the
 * PDF in an in-page overlay instead of opening a new tab.
 */
const ViewReportButton: React.FC<DownloadReportButtonProps> = ({ assessmentId, size = "sm" }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);

  const handleView = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { url } = await api.get<{ url: string }>(
        `/mba-readiness/admin/assessments/${assessmentId}/report-url`,
      );
      setViewUrl(url);
    } catch (err: any) {
      setError(err?.message ?? "Could not get the report link");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (!viewUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setViewUrl(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewUrl]);

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleView();
        }}
        disabled={loading}
        className={cn(
          "inline-flex items-center gap-1.5 font-medium rounded-md border px-2.5 py-1.5 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed",
          size === "sm" ? "text-sm" : "text-xs",
        )}
      >
        {loading ? (
          <Loader2 className={cn("animate-spin", size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3")} />
        ) : (
          <Eye className={size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3"} />
        )}
        View PDF
      </button>
      {error && (
        <span className="inline-flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {error}
        </span>
      )}
      {viewUrl &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-2 sm:p-6"
            onClick={(e) => {
              e.stopPropagation();
              setViewUrl(null);
            }}
          >
            <div
              className="relative flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
                <h3 className="text-sm font-semibold text-gray-900">MBA Readiness Report</h3>
                <button
                  onClick={() => setViewUrl(null)}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <iframe src={viewUrl} title="MBA Readiness Report" className="h-full w-full flex-1" />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

interface SendReportButtonProps {
  assessmentId: string;
  status: AssessmentStatus;
  sending: boolean;
  feedback: { id: string; ok: boolean; message: string } | null;
  onSend: (id: string) => void;
  size?: "sm" | "xs";
}

const SendReportButton: React.FC<SendReportButtonProps> = ({
  assessmentId,
  status,
  sending,
  feedback,
  onSend,
  size = "sm",
}) => {
  const isThisFeedback = feedback?.id === assessmentId;
  return (
    <div className="flex flex-col items-start gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSend(assessmentId);
        }}
        disabled={sending}
        className={cn(
          "inline-flex items-center gap-1.5 font-medium rounded-md border px-2.5 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed",
          size === "sm" ? "text-sm" : "text-xs",
          status === "report_sent"
            ? "border-gray-300 text-gray-700 hover:bg-gray-50"
            : "border-primary text-primary hover:bg-primary/5",
        )}
      >
        {sending ? (
          <Loader2 className={cn("animate-spin", size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3")} />
        ) : (
          <Send className={size === "sm" ? "h-3.5 w-3.5" : "h-3 w-3"} />
        )}
        {status === "report_sent" ? "Resend via WhatsApp" : "Send via WhatsApp"}
      </button>
      {isThisFeedback && (
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs",
            feedback!.ok ? "text-emerald-600" : "text-red-600",
          )}
        >
          {feedback!.ok ? (
            <CheckCircle2 className="h-3 w-3 shrink-0" />
          ) : (
            <AlertCircle className="h-3 w-3 shrink-0" />
          )}
          {feedback!.message}
        </span>
      )}
    </div>
  );
};

interface AssessmentDetailModalProps {
  detail: AssessmentDetail | null;
  loading: boolean;
  onClose: () => void;
  sending: boolean;
  feedback: { id: string; ok: boolean; message: string } | null;
  onSend: (id: string) => void;
}

const AssessmentDetailModal: React.FC<AssessmentDetailModalProps> = ({
  detail,
  loading,
  onClose,
  sending,
  feedback,
  onSend,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-4 sm:px-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Assessment Detail
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading || !detail ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs text-gray-400">Student</p>
                <p className="text-sm font-medium text-gray-900">
                  {detail.student?.name ?? "Unknown"}
                </p>
                {detail.student?.phoneNumber && (
                  <p className="text-xs text-gray-500">{detail.student.phoneNumber}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-400">CAT Percentile</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatNumber(detail.catPercentile)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Academic %</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatNumber(detail.academicPercentage)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Budget Max</p>
                <p className="text-sm font-medium text-gray-900">
                  {detail.budgetMax !== null ? `₹${formatNumber(detail.budgetMax)}` : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Pass ID</p>
                <p className="text-sm font-medium text-gray-900">{detail.passId ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Profile Score</p>
                <p className="text-sm font-medium text-gray-900">
                  {detail.profileScore !== null ? `${formatNumber(detail.profileScore)}/100` : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Fee Savings</p>
                <p className="text-sm font-medium text-gray-900">
                  {detail.totalFeeSavingsInr !== null
                    ? `₹${formatNumber(detail.totalFeeSavingsInr)}/yr`
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Status</p>
                <span
                  className={cn(
                    "inline-flex mt-0.5 px-2 py-1 rounded-full text-xs font-medium",
                    STATUS_META[detail.status].className,
                  )}
                >
                  {STATUS_META[detail.status].label}
                </span>
              </div>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Report Delivery
              </p>
              <div className="flex flex-wrap gap-2">
                <SendReportButton
                  assessmentId={detail.id}
                  status={detail.status}
                  sending={sending}
                  feedback={feedback}
                  onSend={onSend}
                />
                <ViewReportButton assessmentId={detail.id} />
                <DownloadReportButton assessmentId={detail.id} />
              </div>
            </div>

            {detail.overallSummary && (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Overall Summary
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {detail.overallSummary}
                </p>
              </div>
            )}

            {detail.actionPlan && detail.actionPlan.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Action Plan
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {detail.actionPlan.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Per-College Bands ({detail.results?.length ?? 0})
              </p>
              <div className="space-y-3">
                {(detail.results ?? []).map((r) => (
                  <div
                    key={r.id}
                    className="rounded-md border border-gray-200 p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900">
                        {r.college?.name ?? "Unknown college"}
                      </p>
                      <span
                        className={cn(
                          "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                          BAND_META[r.band].className,
                        )}
                      >
                        {BAND_META[r.band].label} · {formatNumber(r.score)}
                      </span>
                    </div>
                    {r.feeWaiverPercent !== null && Number(r.feeWaiverPercent) > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Fee waiver: {formatNumber(r.feeWaiverPercent)}%
                        {r.feeSavingsInr !== null &&
                          ` (save ₹${formatNumber(r.feeSavingsInr)}/yr)`}
                      </p>
                    )}
                    {r.narrative && (
                      <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">
                        {r.narrative}
                      </p>
                    )}
                  </div>
                ))}
                {(!detail.results || detail.results.length === 0) && (
                  <p className="text-sm text-gray-400 italic">No college results</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MbaReadiness;
